import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useOwnerBonus, withBonus } from "./hooks/useOwnerBonus";
import { todayLocalIso } from "./lib/dateHelpers";
import "@/App.css";
import { RotateCcw, Trophy, AlertCircle, XCircle, LayoutGrid } from "lucide-react";
import { Toaster, toast } from "sonner";
import { BrandLogo } from "./components/BrandLogo";
import { HiddenColsLock, useHiddenCols } from "./components/HiddenColsLock";
import { OwnerInvitesButton, OwnerInvitesPanel } from "./components/OwnerInvites";
import { isOwner } from "./components/InviteGate";
import {
  QuickStartButton,
  QuickStartPanel,
  useQuickStartEvents,
} from "./components/QuickStart";
import { MtpBanner } from "./components/MtpBanner";
import { NextRaceLineup } from "./components/NextRaceLineup";
import { CollapsibleSection } from "./components/CollapsibleSection";
import { RaceTable } from "./components/RaceTable";
import { OptimalTicket } from "./components/OptimalTicket";
import { OptimalExoticsTicket } from "./components/OptimalExoticsTicket";
import { RaceGlance } from "./components/RaceGlance";
import { GlancePerformance } from "./components/GlancePerformance";
import { TrackLoader } from "./components/TrackLoader";
import { LateMoneyDetector } from "./components/LateMoneyDetector";
import { ExactaOverlays } from "./components/ExactaOverlays";
import { TicketHistoryPanel } from "./components/TicketHistoryPanel";
import { PerformanceReport } from "./components/PerformanceReport";
import {
  buildBlankLeftRows,
  buildBlankRightRows,
  handicapLeft,
  leftRankMap,
  rightRankMap,
  diffRanks,
  handicapRight,
  hasValidLeft,
  hasValidRight,
} from "./lib/excelHelpers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pill = ({ children }) => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-[0.18em] bg-stone-900 text-[#f5ecd6]"
    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
  >
    {children}
  </span>
);

const ActionButton = ({ testId, onClick, children, variant = "primary", icon: Icon, disabled }) => {
  const base =
    "inline-flex items-center justify-center gap-2 px-5 h-11 rounded-md text-sm font-bold tracking-[0.14em] uppercase transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed";
  const styles = {
    handicap:
      "bg-[#15803d] text-white hover:bg-[#166534] border border-[#0a3d1d] shadow-[0_3px_0_#0a3d1d] hover:translate-y-[1px] hover:shadow-[0_2px_0_#0a3d1d]",
    reset:
      "bg-[#1d4ed8] text-white hover:bg-[#1e40af] border border-[#0c1f6b] shadow-[0_3px_0_#0c1f6b] hover:translate-y-[1px] hover:shadow-[0_2px_0_#0c1f6b]",
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${styles[variant]}`}
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
    >
      {Icon && <Icon size={16} strokeWidth={2.5} />}
      {children}
    </button>
  );
};

const MatrixFootnote = () => (
  <div
    className="mt-6 pt-4 border-t border-stone-800 space-y-1.5 text-[11px] leading-5 text-stone-500 max-w-3xl mx-auto"
    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
    data-testid="footnote"
  >
    <p className="text-stone-400" data-testid="app-subtitle">
      Left half is the 1st leg of the Daily Double, right half is the 2nd leg. Each half is ranked
      by IQ Power on its own, highest first.
    </p>
    <p data-testid="paste-hint">
      Click any <span className="text-[#f87171]">left saddle</span> to eliminate a horse · click any{" "}
      <span className="text-[#facc15]">right saddle</span> to lock a Leg-2 key · press{" "}
      <span className="text-[#f5ecd6]">⌘/Ctrl + V</span> anywhere to paste an odds screenshot.
    </p>
    <p>
      Use <span className="text-[#f5ecd6]">↑ ↓ ← →</span> (or <span className="text-[#f5ecd6]">Tab</span> /{" "}
      <span className="text-[#f5ecd6]">Enter</span>) to move between cells. Rows with no valid price show{" "}
      <span className="text-stone-300">—</span> and drop to the bottom. <span className="text-[#7dd3fc]">Reset</span>{" "}
      clears all values.
    </p>
  </div>
);

function App() {
  const [leftRows, setLeftRows] = useState(buildBlankLeftRows(15));
  const [rightRows, setRightRows] = useState(buildBlankRightRows(15));
  const [handicapped, setHandicapped] = useState(false);
  const [scanning, setScanning] = useState(false);
  // Manual "Must Include" locks for Leg 2 (SLPO side) — post numbers (S2
  // values) the user pins into DD leg 2.
  const [leg2Locks, setLeg2Locks] = useState(() => new Set());
  // Manual eliminations on the LEFT side — post numbers the user has
  // crossed out. Eliminated horses are excluded from DD Leg 1, Exacta,
  // and Trifecta consideration.
  const [leftElims, setLeftElims] = useState(() => new Set());
  // Supported tracks (slug + name) fetched once; used to populate the
  // Track dropdown inside each ticket card's inline target editor.
  const [tracks, setTracks] = useState([]);
  // Globally-locked Key horse (post number). Set via a key-dot click
  // on a LEFT saddle in the DD Matrix OR via a saddle click in the
  // Exacta Overlay matrix. Drives: (a) the DD matrix row ring, (b)
  // the Exacta Overlay focus/dim, (c) the Trifecta "key" in the
  // Optimal Exotics card. One key at a time — toggling re-picks.
  const [keyPost, setKeyPost] = useState(null);
  const toggleKey = useCallback((post) => {
    const n = Number(post);
    if (!Number.isFinite(n)) return;
    setKeyPost((cur) => (cur === n ? null : n));
  }, []);

  // Imperative handle for the MTP banner → TrackLoader click-to-load
  // flow. Each banner click sets a new {slug, race, nonce}; TrackLoader
  // keys its effect on the ref so each click re-fires One-Click Ticket.
  const [pendingLoad, setPendingLoad] = useState(null);
  // The ONE globally selected race. Everything downstream (Live
  // Lineup, DD Matrix, Late Money, Exacta Overlays, Optimal tickets)
  // is scoped to this. null = fresh/cleared state → the UI shows only
  // the "pick a track to begin" prompt.
  const [activeTrack, setActiveTrack] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lineupInfo, setLineupInfo] = useState(null);
  const [colsUnlocked, setColsUnlocked] = useHiddenCols();
  // Rank movement since the previous handicap (auto-refresh or manual).
  const [rankMoves, setRankMoves] = useState({ left: {}, right: {} });
  const lastRanksRef = useRef({ left: new Map(), right: new Map() });
  const resetRankMoves = useCallback(() => {
    lastRanksRef.current = { left: new Map(), right: new Map() };
    setRankMoves({ left: {}, right: {} });
  }, []);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const closeInvites = useCallback(() => setInvitesOpen(false), []);
  const [helpFocus, setHelpFocus] = useState({ step: null, nonce: 0 });
  const closeHelp = useCallback(() => setHelpOpen(false), []);
  useQuickStartEvents(
    useCallback((step) => {
      setHelpOpen(true);
      setHelpFocus({ step, nonce: Date.now() });
    }, [])
  );
  // Collapsible state for the DD matrix. Opens when the user picks a
  // chip or triggers a Load.
  const [matrixOpen, setMatrixOpen] = useState(false);
  const onPickTrack = useCallback((t) => {
    if (!t?.slug) return;
    setActiveTrack({
      slug: t.slug,
      name: t.name || t.slug,
      race: t.race ?? null,
      mtp_minutes: t.mtp_minutes ?? null,
      post_time: t.post_time ?? null,
    });
    setPendingLoad({ slug: t.slug, race: t.race ?? null, nonce: Date.now() });
    setMatrixOpen(true);
    toast.message(
      `${t.name || t.slug} · Race ${t.race ?? "?"} — loading One-Click Ticket…`
    );
  }, []);
  const [trackContext, setTrackContext] = useState({
    name: "",
    slug: "",
    race: "",
    date: "",
  });
  // New track or race → movement arrows start fresh.
  useEffect(() => {
    resetRankMoves();
  }, [trackContext.slug, trackContext.race, resetRankMoves]);
  const applyTrackContext = useCallback((ctx) => {
    setTrackContext(ctx);
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API}/tracks`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (Array.isArray(data.tracks)) setTracks(data.tracks);
      } catch {
        /* ignore — editor still usable with free-text */
      }
    })();
  }, []);

  // Called from the ticket cards' inline "edit target" popover. Merges
  // the override into trackContext so DD, Exacta, and Trifecta all pick
  // up the new track + race without re-fetching live odds.
  const onEditTarget = ({ track_slug, name, race }) => {
    setTrackContext((prev) => ({
      ...prev,
      slug: track_slug || prev.slug,
      name: name || prev.name,
      race: race != null ? String(race) : prev.race,
    }));
  };

  const toggleLock = (setter) => (post) => {
    const n = Number(post);
    if (!Number.isFinite(n)) return;
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };
  const toggleLeg2Lock = toggleLock(setLeg2Locks);
  const toggleLeftElim = toggleLock(setLeftElims);

  // Global paste handler: if the clipboard contains an image, send it to
  // /api/scan-odds and fill the DD column of matching S rows.
  useEffect(() => {
    const onPaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      let imgItem = null;
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          imgItem = it;
          break;
        }
      }
      if (!imgItem) return; // not an image paste — let default behavior run
      e.preventDefault();
      const file = imgItem.getAsFile();
      if (!file) return;

      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      setScanning(true);
      const toastId = toast.loading("Scanning screenshot…");
      try {
        const resp = await fetch(`${API}/scan-odds`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image_base64: b64 }),
        });
        if (!resp.ok) {
          let detail = `HTTP ${resp.status}`;
          try {
            const errBody = await resp.json();
            if (errBody?.detail) detail = errBody.detail;
          } catch {
            /* leave default */
          }
          throw new Error(detail);
        }
        const data = await resp.json();
        const entries = data.entries || [];
        const scanType = String(data.type || "DD").toUpperCase();
        if (entries.length === 0) {
          toast.error("No entries found in that image.", { id: toastId });
          return;
        }

        if (scanType === "ML") {
          setLeftRows((prev) => {
            const next = prev.slice();
            let filled = 0;
            for (const ent of entries) {
              const s = Number(ent.S);
              if (!Number.isInteger(s) || s < 1) continue;
              while (next.length < s && next.length < 20) {
                next.push({ RANK: "", S: next.length + 1, DD: "", ML: "" });
              }
              const idx = next.findIndex((r) => Number(r.S) === s);
              if (idx === -1) continue;
              const v = String(ent.value || "").trim();
              if (v) {
                next[idx] = { ...next[idx], ML: v };
                filled += 1;
              }
            }
            if (filled === 0) {
              toast.error("Read the image, but no ML values to fill.", {
                id: toastId,
              });
            } else {
              toast.success(
                `Filled ${filled} ML value${filled === 1 ? "" : "s"} from screenshot.`,
                { id: toastId }
              );
            }
            return next;
          });
        } else {
          setLeftRows((prev) => {
            const next = prev.slice();
            let filled = 0;
            for (const ent of entries) {
              const s = Number(ent.S);
              if (!Number.isInteger(s) || s < 1) continue;
              while (next.length < s && next.length < 20) {
                next.push({ RANK: "", S: next.length + 1, DD: "", ML: "" });
              }
              const idx = next.findIndex((r) => Number(r.S) === s);
              if (idx === -1) continue;
              const v = String(ent.value || "").trim();
              if (v) {
                next[idx] = { ...next[idx], DD: v, DDFlag: null };
                filled += 1;
              }
            }
            if (filled === 0) {
              toast.error("Read the image, but no DD values to fill.", {
                id: toastId,
              });
            } else {
              toast.success(
                `Filled ${filled} DD value${filled === 1 ? "" : "s"} from screenshot.`,
                { id: toastId }
              );
            }
            return next;
          });
        }
        if (handicapped) setHandicapped(false);
      } catch (err) {
        console.error(err);
        const msg = String(err?.message || err || "");
        // Try to surface the backend's `detail` field so budget / rate-limit
        // problems don't look like a generic scan failure.
        let friendly = "Screenshot scan failed. Try a clearer image.";
        try {
          const parsed = JSON.parse(msg);
          if (parsed?.detail) friendly = parsed.detail;
        } catch {
          if (msg && msg.length < 250) friendly = msg;
        }
        toast.error(friendly, { id: toastId, duration: 8000 });
      } finally {
        setScanning(false);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [handicapped]);

  const onLeftChange = (rowIdx, field, value) => {
    setLeftRows((prev) => {
      const next = prev.slice();
      next[rowIdx] = { ...next[rowIdx], [field]: value };
      if (field === "DD") next[rowIdx].DDFlag = null;
      return next;
    });
    if (handicapped) setHandicapped(false);
  };

  const onRightChange = (rowIdx, field, value) => {
    setRightRows((prev) => {
      const next = prev.slice();
      next[rowIdx] = { ...next[rowIdx], [field]: value };
      return next;
    });
    if (handicapped) setHandicapped(false);
  };

  const [bonus, saveBonus] = useOwnerBonus({
    slug: trackContext.slug || activeTrack?.slug,
    race: trackContext.race || activeTrack?.race,
    date: trackContext.date || todayLocalIso(),
  });
  const leftRowsB = useMemo(() => withBonus(leftRows, bonus.left, "S"), [leftRows, bonus.left]);
  const rightRowsB = useMemo(() => withBonus(rightRows, bonus.right, "S2"), [rightRows, bonus.right]);

  const onHandicap = ({ silent = false } = {}) => {
    const okL = hasValidLeft(leftRowsB);
    const okR = hasValidRight(rightRowsB);
    if (!okL && !okR) {
      if (!silent) toast.warning("Enter odds values first.");
      return;
    }
    const nl = handicapLeft(leftRowsB);
    const nr = handicapRight(rightRowsB);
    const next = { left: leftRankMap(nl), right: rightRankMap(nr) };
    setRankMoves({
      left: diffRanks(lastRanksRef.current.left, next.left),
      right: diffRanks(lastRanksRef.current.right, next.right),
    });
    lastRanksRef.current = next;
    setLeftRows(nl);
    setRightRows(nr);
    setHandicapped(true);
    if (!silent)
      toast.success("Handicapped — each half ranked by IQ Power independently.", { id: "handicap" });
  };

  // Owner points changed (or arrived) → re-rank silently for everyone.
  useEffect(() => {
    if (handicapped) onHandicap({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bonus]);


  const onReset = () => {
    setLeftRows(buildBlankLeftRows(15));
    setRightRows(buildBlankRightRows(15));
    setLeg2Locks(new Set());
    setLeftElims(new Set());
    setHandicapped(false);
    toast("All values cleared. Order restored.");
  };

  // "Clear All" from the Live Board banner. Nukes everything scoped to
  // the current race so the user is ready for the next pick — but
  // leaves Ticket History untouched (it's the long-term store).
  const onClearAll = useCallback(() => {
    resetRankMoves();
    setActiveTrack(null);
    setPendingLoad(null);
    setMatrixOpen(false);
    setLeftRows(buildBlankLeftRows(15));
    setRightRows(buildBlankRightRows(15));
    setLeg2Locks(new Set());
    setLeftElims(new Set());
    setKeyPost(null);
    setHandicapped(false);
    setTrackContext({ name: "", slug: "", race: "", date: "" });
    toast("Cleared — ready for the next race. Ticket History kept.");
  }, []);

  return (
    <div
      className="min-h-screen w-full"
      data-testid="app-root"
      style={{
        background: "radial-gradient(1200px 600px at 80% -10%, #2a2a2a 0%, #111 50%, #0a0a0a 100%)",
      }}
    >
      <Toaster position="top-center" richColors theme="dark" visibleToasts={2} />

      <MtpBanner
        apiBase={API}
        onPickTrack={onPickTrack}
        activeSlug={activeTrack?.slug || null}
        onClearAll={onClearAll}
      />

      <header className="px-6 sm:px-10 py-6 border-b border-stone-700/40" data-testid="app-header">
        <div className="max-w-6xl mx-auto flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Pill>Form 001</Pill>
              <span
                className="text-stone-500 text-[11px] tracking-[0.2em] uppercase"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
              >
                Post-time tool
              </span>
            </div>
            <BrandLogo size="md" />
          </div>
          <div className="flex items-center gap-2">
            {(isOwner() || colsUnlocked) && (
              <OwnerInvitesButton onClick={() => setInvitesOpen((v) => !v)} />
            )}
            <QuickStartButton onClick={() => setHelpOpen((v) => !v)} />
          </div>
        </div>
      </header>
      <QuickStartPanel open={helpOpen} onClose={closeHelp} focus={helpFocus} />
      <OwnerInvitesPanel open={invitesOpen} onClose={closeInvites} />

      <NextRaceLineup apiBase={API} activeTrack={activeTrack} onPickTrack={onPickTrack} onInfo={setLineupInfo} />
      {activeTrack && handicapped && (
        <RaceGlance
          leftRows={leftRowsB}
          leftElims={leftElims}
          keyPost={keyPost}
          track={lineupInfo?.track || activeTrack.name}
          race={trackContext.race || activeTrack.race}
          postTime={lineupInfo?.postTime}
          mtp={lineupInfo?.mtp ?? activeTrack.mtp_minutes}
          trackSlug={trackContext.slug || activeTrack.slug}
          date={trackContext.date}
          owner={colsUnlocked}
        />
      )}

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        {!activeTrack ? (
          <div
            className="rounded-lg border border-dashed border-stone-700 bg-[#0e0e0e] px-8 py-16 text-center"
            data-testid="pick-a-track-prompt"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#84cc16] mb-3">
              Step 1
            </div>
            <h2
              className="text-2xl sm:text-3xl text-[#f5ecd6] mb-2"
              style={{
                fontFamily: "'Instrument Serif', 'Times New Roman', serif",
                fontWeight: 400,
                letterSpacing: "-0.01em",
              }}
            >
              Pick a track to begin
            </h2>
            <p className="text-stone-400 text-[12px] max-w-md mx-auto leading-6">
              Click any track chip in the Live Board above. That track becomes
              the single focus — the Live Lineup, DD Matrix, Late Money, and
              Exacta Overlays all follow. Use <span className="text-[#fca5a5]">Clear All</span> in the banner to reset for the next race.
            </p>
          </div>
        ) : (
          <>
            <TrackLoader
              apiBase={API}
              setLeftRows={setLeftRows}
              setRightRows={setRightRows}
              leftRows={leftRowsB}
              handicapped={handicapped}
              onFilled={() => handicapped && setHandicapped(false)}
              onLeg2Filled={() => handicapped && setHandicapped(false)}
              onHandicap={onHandicap}
              onTrackContext={applyTrackContext}
              onLoaded={() => setMatrixOpen(true)}
              pendingLoad={pendingLoad}
              auto={autoRefresh}
            />

            <CollapsibleSection
              testId="dd-matrix-section"
              title="DD Matrix · 2-Leg Handicap Board"
              helpStep={3}
              icon={LayoutGrid}
              open={matrixOpen}
              onToggle={() => setMatrixOpen((v) => !v)}
              badge={
                <>
                  {activeTrack && (
                    <span
                      className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#1a2e05] text-[#a3e635] border border-[#365314] font-bold tracking-[0.14em]"
                      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                      data-testid="dd-matrix-target"
                    >
                      {activeTrack.name}
                      {trackContext.race
                        ? ` · R${trackContext.race}`
                        : activeTrack.race
                        ? ` · R${activeTrack.race}`
                        : ""}
                    </span>
                  )}
                  {handicapped && (
                    <span
                      className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#84cc16] text-[#0a0a0a] font-bold tracking-[0.14em]"
                      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                    >
                      Handicapped
                    </span>
                  )}
                </>
              }
            >
              <div className="flex flex-wrap items-center justify-end gap-3 mb-5" data-testid="meta-row">
                <label
                  className="mr-auto inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-stone-300 cursor-pointer select-none"
                  style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                  data-testid="track-auto-toggle"
                >
                  <input
                    type="checkbox"
                    data-testid="track-auto-checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="accent-[#84cc16] w-4 h-4"
                  />
                  Auto-refresh 30s
                  {autoRefresh && (
                    <span className="inline-flex h-2 w-2 rounded-full bg-[#84cc16] animate-pulse" />
                  )}
                </label>
                <div className="flex items-center gap-3 flex-wrap">
                  {leftElims.size > 0 && (
                    <button
                      type="button"
                      data-testid="btn-clear-elims"
                      onClick={() => {
                        setLeftElims(new Set());
                        toast("Eliminations cleared.");
                      }}
                      className="inline-flex items-center gap-1.5 px-3 h-7 rounded-md text-[10px] font-bold tracking-[0.18em] uppercase transition-colors border border-[#7f1d1d] text-[#fca5a5] hover:bg-[#7f1d1d] hover:text-white"
                      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                      title={`Clear all ${leftElims.size} elimination${leftElims.size === 1 ? "" : "s"}`}
                    >
                      <XCircle size={12} />
                      Clear elims · {leftElims.size}
                    </button>
                  )}
                  {handicapped ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-[#84cc16] text-xs uppercase tracking-[0.2em]"
                      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                      data-testid="status-handicapped"
                    >
                      <Trophy size={14} /> Handicapped
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 text-stone-400 text-xs uppercase tracking-[0.2em]"
                      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                      data-testid="status-original"
                    >
                      <AlertCircle size={14} /> Original order
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 mb-2" data-testid="hidden-cols-bar">
                <HiddenColsLock unlocked={colsUnlocked} onChange={setColsUnlocked} />
              </div>
              <RaceTable
                showHidden={colsUnlocked}
                rankMoves={rankMoves}
                leftRows={leftRowsB}
                rightRows={rightRowsB}
                onLeftChange={onLeftChange}
                onRightChange={onRightChange}
                leftElims={leftElims}
                leg2Locks={leg2Locks}
                onToggleLeftElim={toggleLeftElim}
                onToggleLeg2Lock={toggleLeg2Lock}
                keyPost={keyPost}
                onToggleKey={toggleKey}
                onBonus={saveBonus}
              />

              <div
                className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4"
                data-testid="action-bar"
              >
                <ActionButton testId="btn-handicap" onClick={onHandicap} variant="handicap" icon={Trophy}>
                  Handicap
                </ActionButton>
                <ActionButton testId="btn-reset" onClick={onReset} variant="reset" icon={RotateCcw}>
                  Reset
                </ActionButton>
              </div>
              <MatrixFootnote />
            </CollapsibleSection>

            <ExactaOverlays
              apiBase={API}
              trackSlug={trackContext.slug || activeTrack.slug}
              trackName={activeTrack.name}
              race={trackContext.race || activeTrack.race}
              leftRows={leftRowsB}
              leftElims={leftElims}
              keyPost={keyPost}
              onToggleKey={toggleKey}
              date={trackContext.date}
            />
            <LateMoneyDetector
              apiBase={API}
              trackSlug={trackContext.slug || activeTrack.slug}
              trackName={activeTrack.name}
              race={trackContext.race || activeTrack.race}
            />

            <OptimalTicket
              leftRows={leftRowsB}
              rightRows={rightRowsB}
              handicapped={handicapped}
              trackName={trackContext.name || activeTrack.name}
              trackSlug={trackContext.slug || activeTrack.slug}
              race={trackContext.race || activeTrack.race}
              date={trackContext.date}
              leftElims={leftElims}
              leg2Locks={leg2Locks}
              tracks={tracks}
              onEditTarget={onEditTarget}
            />

            <OptimalExoticsTicket
              leftRows={leftRowsB}
              handicapped={handicapped}
              trackName={trackContext.name || activeTrack.name}
              trackSlug={trackContext.slug || activeTrack.slug}
              race={trackContext.race || activeTrack.race}
              date={trackContext.date}
              leftElims={leftElims}
              keyPost={keyPost}
              tracks={tracks}
              onEditTarget={onEditTarget}
            />
          </>
        )}

        <PerformanceReport />

        <TicketHistoryPanel apiBase={API} />
        {colsUnlocked && <GlancePerformance />}

      </main>
    </div>
  );
}

export default App;
